import React, { useContext, useState, useMemo } from "react";

import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";
import { SearchableDetailsDropdown } from "../../../../components/ui";

import { GoGear, GoClock } from "react-icons/go";
import { LiaTapeSolid } from "react-icons/lia";
import { GiMineTruck } from "react-icons/gi";
import { PiTrash } from "react-icons/pi";

/**
 * Editable form row for adding or updating one scope detail entry (concrete,
 * equipment, subcontractor, or dumping). Uses {@link DispatchContext} catalog
 * data for searchable dropdowns where applicable.
 *
 * @param {Object} props
 * @param {number} props.index Row index within the parent detail list.
 * @param {string} props.type Detail kind (`concrete`, `equipment`, `subcontractor`, `dumping`).
 * @param {string} props.mode `Edit` or `Add`.
 * @param {string} [props.variant="dark"] Color theme (`dark` or `light`).
 * @param {Function} props.onChange Called when field values change.
 * @param {Function} props.onDelete Called when the row is removed.
 * @param {Function} props.onAddMultiple Called when multi-select adds several catalog items.
 * @param {string} props.className Additional wrapper classes.
 * @param {Object} [props.initialValues={}] Seed values for the row.
 * @returns {JSX.Element}
 */
const DetailsBoxUpdate = ({
  index,
  type,
  mode,
  variant = "dark",
  onChange,
  onDelete,
  onAddMultiple,
  className,
  initialValues = {},
}) => {
  // Default "dark" colors
  var colors = {
    bg: "bg-green-700",
    text: "text-white",
    title: "text-white",
  };

  if (variant === "light") {
    colors = {
      bg: "",
      text: "",
      title: "",
      outline: "",
      input: "",
    };
  }

  // A bit of a departure from the 'stateless' details boxes, but is needed to update
  // the supplier name when a concrete mix is selected.
  const { modelReferenceData } = useContext(DispatchContext);
  const [mixSupplier, setMixSupplier] = useState(
    initialValues.mix?.supplier?.name || ""
  );

  const [isVisible, setIsVisible] = useState(true); // Hide on delete until API update
  /**
   * Hide this row immediately, then ask the parent to persist the deletion.
   *
   * @param {void}
   * @returns {void}
   */
  const handleDelete = () => {
    setIsVisible(false);
    onDelete();
  };

  /**
   * Coerce a catalog id to a number for dropdown values, or `""` when empty/invalid.
   *
   * @param {string|number|null|undefined} id Raw catalog id.
   * @returns {number|string}
   */
  const idToValue = (id) => {
    if (id === null || id === undefined || id === "") return "";
    const num = Number(id);
    return isNaN(num) ? "" : num;
  };

  /**
   * True when this row is still a blank add-placeholder (no catalog item chosen).
   *
   * @param {void}
   * @returns {boolean}
   */
  const isEmptyItem = useMemo(() => {
    if (type === "concrete") {
      return !initialValues.mix?.id;
    } else if (type === "equipment") {
      return !initialValues.asset?.id;
    } else if (type === "subcontractor" || type === "dumping") {
      return !initialValues.contractor?.id;
    }
    return false;
  }, [type, initialValues]);

  /**
   * Map equipment catalog rows into searchable dropdown options.
   *
   * @param {void}
   * @returns {Array<{ id: number, label: string, tooltip?: string }>}
   */
  const equipmentOptions = useMemo(() => {
    return (
      modelReferenceData.equipment?.map((item) => ({
        id: Number(item.id),
        label: item.name,
        tooltip: item.notes,
      })) || []
    );
  }, [modelReferenceData.equipment]);

  /**
   * Apply one or many equipment selections; empty multi-select clears the row.
   *
   * @param {number|string|Array<number|string>} selectedIdOrIds Catalog id or ids.
   * @returns {void}
   */
  const handleEquipmentChange = (selectedIdOrIds) => {
    // Handle multi-select (array of IDs)
    if (Array.isArray(selectedIdOrIds)) {
      if (selectedIdOrIds.length === 0) {
        onChange({ equipment: {} });
        return;
      }
      // Create detail data for each selected equipment
      const itemsToAdd = selectedIdOrIds.map((selectedId) => {
        const selectedEquipment = modelReferenceData.equipment?.find(
          (item) => item.id === Number(selectedId)
        );
        return {
          equipment: {
            id: Number(selectedEquipment.id),
            name: selectedEquipment.name,
          },
          rent: false, // Default value
        };
      });
      onAddMultiple?.(itemsToAdd);
      return;
    }

    // Handle single selection
    const selectedEquipment = modelReferenceData.equipment?.find(
      (item) => item.id === Number(selectedIdOrIds)
    );
    if (selectedEquipment) {
      onChange({
        equipment: {
          id: Number(selectedEquipment.id),
          name: selectedEquipment.name,
        },
      });
    } else {
      onDelete({ asset: {} });
    }
  };

  /**
   * Map subcontractor catalog rows into searchable dropdown options.
   *
   * @param {void}
   * @returns {Array<{ id: number, label: string }>}
   */
  const subcontractorOptions = useMemo(() => {
    return (
      modelReferenceData.subcontractor?.map((item) => ({
        id: Number(item.id),
        label: item.name,
      })) || []
    );
  }, [modelReferenceData.subcontractor]);

  /**
   * Apply one or many subcontractor selections; empty multi-select clears the row.
   *
   * @param {number|string|Array<number|string>} selectedIdOrIds Catalog id or ids.
   * @returns {void}
   */
  const handleSubcontractorChange = (selectedIdOrIds) => {
    // Handle multi-select (array of IDs)
    if (Array.isArray(selectedIdOrIds)) {
      if (selectedIdOrIds.length === 0) {
        onChange({ contractor: {} });
        return;
      }
      // Create detail data for each selected subcontractor
      const itemsToAdd = selectedIdOrIds.map((selectedId) => {
        const selectedContractor = modelReferenceData.subcontractor?.find(
          (item) => item.id === Number(selectedId)
        );
        return {
          contractor: {
            id: Number(selectedContractor.id),
            name: selectedContractor.name,
          },
          hours: 0, // Default value
        };
      });
      onAddMultiple?.(itemsToAdd);
      return;
    }

    // Handle single selection
    const selectedContractor = modelReferenceData.subcontractor?.find(
      (item) => item.id === Number(selectedIdOrIds)
    );
    if (selectedContractor) {
      onChange({
        contractor: {
          id: Number(selectedContractor.id),
          name: selectedContractor.name,
        },
      });
    } else {
      onChange({ contractor: {} });
    }
  };

  /**
   * Map dumping catalog rows into searchable dropdown options.
   *
   * @param {void}
   * @returns {Array<{ id: number, label: string }>}
   */
  const dumpingOptions = useMemo(() => {
    return (
      modelReferenceData.dumping?.map((item) => ({
        id: Number(item.id),
        label: item.name,
      })) || []
    );
  }, [modelReferenceData.dumping]);

  /**
   * Apply one or many dumping-contractor selections; empty multi-select clears the row.
   *
   * @param {number|string|Array<number|string>} selectedIdOrIds Catalog id or ids.
   * @returns {void}
   */
  const handleDumpingChange = (selectedIdOrIds) => {
    // Handle multi-select (array of IDs)
    if (Array.isArray(selectedIdOrIds)) {
      if (selectedIdOrIds.length === 0) {
        onChange({ contractor: {} });
        return;
      }
      // Create detail data for each selected dumping contractor
      const itemsToAdd = selectedIdOrIds.map((selectedId) => {
        const selectedContractor = modelReferenceData.dumping?.find(
          (item) => item.id === Number(selectedId)
        );
        return {
          contractor: {
            id: Number(selectedContractor.id),
            name: selectedContractor.name,
          },
          quantity: 0, // Default value
        };
      });
      onAddMultiple?.(itemsToAdd);
      return;
    }

    // Handle single selection
    const selectedContractor = modelReferenceData.dumping?.find(
      (item) => item.id === Number(selectedIdOrIds)
    );
    if (selectedContractor) {
      onChange({
        contractor: {
          id: Number(selectedContractor.id),
          name: selectedContractor.name,
        },
      });
    } else {
      onChange({ contractor: {} });
    }
  };

  /**
   * Map concrete mix catalog rows into searchable dropdown options.
   *
   * @param {void}
   * @returns {Array<{ id: number, label: string }>}
   */
  const concreteOptions = useMemo(() => {
    return (
      modelReferenceData.concrete?.map((item) => ({
        id: Number(item.id),
        label: item.design_number,
      })) || []
    );
  }, [modelReferenceData.concrete]);

  /**
   * Apply a mix selection and also update local supplier text (parent does not own that).
   *
   * @param {number|string} selectedId Concrete mix catalog id.
   * @returns {void}
   */
  const handleConcreteChange = (selectedId) => {
    const selectedConcreteObj = modelReferenceData.concrete?.find(
      (item) => item.id === Number(selectedId)
    );
    if (selectedConcreteObj) {
      setMixSupplier(selectedConcreteObj.supplier.name);
      onChange({
        mix: {
          id: Number(selectedConcreteObj.id),
          design_number: selectedConcreteObj.design_number,
        },
        supplier: selectedConcreteObj.supplier.name,
      });
    } else {
      setMixSupplier("");
      onChange({ mix: {}, supplier: "" });
    }
  };

  /**
   * Render the shared add/edit field row for this detail type.
   *
   * @param {JSX.Element} fields Type-specific inputs.
   * @param {boolean} [isConcrete=false] Stack fields vertically for mix + supplier.
   * @param {boolean} [isDumping=false] Unused layout flag kept for call-site symmetry.
   * @returns {JSX.Element}
   */
  const renderForm = (fields, isConcrete = false, isDumping = false) => (
    <div className={`w-full ${isVisible ? "" : "hidden"}`}>
      {" "}
      <div className={`flex flex-col rounded mt-1 ${className}`}>
        <div
          key={`${type}-add`}
          className={`flex ${isConcrete ? "flex-col" : "justify-between"} w-full p-0 rounded ${colors["bg"]} ${colors["text"]} ${colors["outline"]}`}
        >
          {isConcrete ? (
            <div className="flex flex-row justify-between">
              <div className="font-bold">
                {mode.charAt(0).toUpperCase() + mode.slice(1)} Concrete{" "}
                {index + 1}
              </div>
              {!isEmptyItem && (
                <div
                  className="cursor-pointer align-middle my-auto hover:bg-red-200 rounded-md duration-300 ease-in-out"
                  onClick={handleDelete}
                >
                  <PiTrash className="my-auto" color="red" size={18} />
                </div>
              )}
            </div>
          ) : null}
          {fields.map((field, index) => (
            <div
              key={index}
              className={`flex h-[22px] ${isConcrete ? "" : ""} ${isDumping ? "w-full" : ""} flex-row align-middle items-center ${isConcrete && index === 3 ? `bg-gray-200` : `${colors["input"]}`}`}
            >
              {field}
            </div>
          ))}
          {!isConcrete && !isEmptyItem && (
            <div
              className="cursor-pointer align-middle my-auto hover:bg-red-200 rounded-md duration-300 ease-in-out"
              onClick={handleDelete}
            >
              <PiTrash className="my-auto" color="red" size={18} />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (type === "concrete") {
    return renderForm(
      [
        <>
          <GoGear className="h-4 mx-1 my-auto" />
          <SearchableDetailsDropdown
            options={concreteOptions}
            value={idToValue(initialValues.mix?.id)}
            onChange={handleConcreteChange}
            placeholder="Select Mix"
            className="w-full"
          />
        </>,
        <>
          <GoClock className="h-4 mx-1 my-auto" />
          <input
            type="time"
            className="w-full bg-transparent h-4 outline-none rounded"
            onChange={(e) => onChange({ time: e.target.value })}
            value={initialValues.time ? initialValues.time : ""}
            step="60"
          />
        </>,
        <>
          <LiaTapeSolid className="h-4 mx-1 my-auto" />
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            placeholder="Enter Yards"
            className="caret-black w-full bg-transparent outline-none rounded"
            onChange={(e) => onChange({ yards: e.target.value })}
            value={initialValues.yards || ""}
          />
        </>,
        <>
          <GiMineTruck className="h-4 mx-1 my-auto" />
          <input
            type="text"
            value={mixSupplier}
            readOnly
            placeholder="Select mix to show"
            className="w-full bg-transparent outline-none rounded"
          />
        </>,
      ],
      true
    );
  }

  if (type === "equipment") {
    return renderForm(
      [
        <div className="flex flex-row gap-x-1 w-full">
          <div className="flex-1">
            <SearchableDetailsDropdown
              options={equipmentOptions}
              multiSelect={true}
              value={idToValue(
                initialValues.operation !== "delete"
                  ? initialValues.equipment?.id || initialValues.asset?.id
                  : ""
              )}
              onChange={handleEquipmentChange}
              placeholder="Select Equipment"
              className="w-full h-[22px]"
              dropdownMinWidth={200}
            />
          </div>
          <div className="w-16">
            <select
              className="p-[2px] bg-transparent text-xs border h-[22px] rounded-md w-full"
              onChange={(e) => onChange({ rent: e.target.value === "true" })}
              value={String(initialValues.rent)}
            >
              <option value="false">Own</option>
              <option value="true">Rent</option>
            </select>
          </div>
        </div>,
      ],
      false,
      false
    );
  }

  if (type === "subcontractor") {
    return renderForm([
      <div className="grid grid-cols-10 gap-x-1">
        <div className="col-span-7 flex align-middle items-center">
          <SearchableDetailsDropdown
            options={subcontractorOptions}
            multiSelect={true}
            value={idToValue(initialValues.contractor?.id)}
            onChange={handleSubcontractorChange}
            placeholder="Subcontractor"
            className="mr-[2px] w-full"
          />
        </div>
        <div className="col-span-3 flex flex-row align-middle items-center">
          <input
            type="number"
            inputMode="decimal"
            step="1"
            placeholder="Hrs"
            className="p-[2px] h-[20px] w-full rounded caret-black outline-none bg-transparent"
            onChange={(e) => onChange({ hours: e.target.value })}
            value={initialValues.hours || ""}
            min="0"
          />
        </div>
      </div>,
    ]);
  }

  if (type === "dumping") {
    return renderForm(
      [
        <div className="grid grid-cols-10 gap-x-1">
          <div className="col-span-7 flex align-middle items-center">
            <SearchableDetailsDropdown
              options={dumpingOptions}
              multiSelect={true}
              value={idToValue(initialValues.contractor?.id)}
              onChange={handleDumpingChange}
              placeholder="Contractor"
              className="mr-[2px] w-full"
            />
          </div>
          <div className="col-span-3 flex flex-row align-middle items-center">
            <input
              type="number"
              inputMode="decimal"
              step="1"
              placeholder="Qty"
              className="p-[2px] h-[20px] w-full rounded caret-black outline-none bg-transparent"
              onChange={(e) => onChange({ quantity: e.target.value })}
              value={initialValues.quantity || ""}
              min="0"
            />
          </div>
        </div>,
      ],
      false,
      true
    );
  }
};

export default DetailsBoxUpdate;
